import {
  VDrivePaymentMode,
  VDrivePreferredAssignmentType,
} from './api/vDrive.types';
// Type only on purpose: `api/location.types` imports this file back, and a
// value import would close a runtime cycle between the two.
import type { ProviderConnectionType } from './api/location.types';
import type { PendingBankSummary } from './api/outstandingBalance.types';
import { Brand, SimpleBrandWithImageEntity, SipDetails } from './brand.types';
import { CustomersOverviewEntity } from './brandOwner';
import { MenuInfo, SimpleMenuProviderEntity } from './menu.types';
import { IntegrationAccess } from './api/integrationAccess.types';
import {
  Coordinates,
  LocationImageEntity,
  Provider,
  ProviderType,
  ProviderValue,
  SalesStatics,
  VDriveProvider,
  VDriveStatus,
} from './shared.types';
import { User, UserRole } from './user.types';

export type SimpleLocationEntity = {
  id: number;
  mode: LocationMode;
  name: string;
  verified: boolean;
  address: string;
  timeZone: string;
  zipCode: string;
  phoneNumber: string | null;
  brands: SimpleBrandWithImageEntity[];
  createdAt: string;
  imageUrl: string | null;
};

export type LocationSettings = {
  prepTime: number | null;
  frontCopies: number;
  backCopies: number;
  onlyBrandReceipt: boolean;
  includePreparationNotes: boolean;
};

export interface LocationVDriveProviderContractEntity {
  provider: Provider;
  beforeCommission: number;
  afterCommission: number;
}

export type Owner = Pick<
  User,
  'id' | 'firstName' | 'lastName' | 'email' | 'role'
> & {
  phone: {
    nationalNumber: string;
    number: string;
  };
};
export type Referral = Pick<
  User,
  'id' | 'firstName' | 'lastName' | 'createdAt' | 'role'
> & {
  phone: {
    nationalNumber: string;
    number: string;
  };
};
export interface Configuration {
  einLast4?: string | null;
  bankAccountLast4?: string | null;
}
export interface VOrdersMonthlySalesFee {
  id: number;
  fee: number;
  agentCommission: number;
}
export interface SalesConfig {
  vDriveService: boolean;
  vDriveDeliveryCharge?: number | null;
  vDriveDeliveryFee?: number | null;
  vOrdersService: boolean;
  vOrdersMonthlySalesFee?: VOrdersMonthlySalesFee | null;
  vGrubsService: boolean;
  extraPrinters: number;
  extraTablets: number;
  totalAmount: number;
  vRevenueService: boolean;
  revenuRecoveryFee?: number | null;
  /**
   * vTablet billed per order instead of monthly: cents ($0.01-$5) withheld
   * from the vGrubs payout of every virtual-brand order. Null = billed
   * monthly. When set, the computed `totalAmount` leaves the monthly vTablet
   * fee out (a total typed in by hand is never changed by the backend).
   */
  vOrdersPerOrderFee?: number | null;
  /**
   * vTablet billed per day instead: cents ($0.01-$5) taken from the vGrubs
   * balance (vPay Card) at 11:00 New York every day, into the negative if
   * need be, up to the monthly vTablet fee in a month. Null = not per day.
   * Never set together with `vOrdersPerOrderFee`. Left out of the computed
   * `totalAmount` like per order.
   */
  vOrdersPerDayFee?: number | null;
}

export enum LocationPayoutService {
  vGrubs = 'vGrubs',
  vOrders = 'vOrders',
  vGrubsAI = 'vGrubsAI',
  kanekt365 = 'kanekt365',
  bytes = 'bytes',
}

export enum PayoutModelType {
  /** Location keeps `percentage` of the base amount. */
  bracket = 'bracket',
  /** vGrubs charges a fixed `flatFee` per order. */
  flat_fee = 'flat_fee',
  /** Percentage and/or flat fee, optionally varying by base-amount tier. */
  hybrid = 'hybrid',
}

/**
 * A per-provider exception. It **replaces** the whole rule of the level it sits
 * on rather than adding to it: a tier of `90% + $3` with an exception of `85%`
 * pays those providers 85% and no flat fee at all.
 */
export interface PayoutProviderRule {
  /**
   * At least one. A provider may appear in only one rule of the same level per
   * order source - see `sources`.
   */
  providers: ProviderType[];
  /**
   * How the provider was connected when the order came in. Uber Eats and
   * Grubhub can arrive over more than one connection and are paid differently
   * per connection, which is what this narrows the exception to. Empty = every
   * source, which is how every exception saved before this field behaves.
   */
  sources: ProviderConnectionType[];
  /** Location's share for these providers, 0-100. */
  percentage: number | null;
  /** Flat fee vGrubs charges for these providers, in cents. */
  flatFee: number | null;
}

export interface PayoutTier {
  /** Lower bound of the base amount window, in cents (inclusive). */
  minAmount: number;
  /** Upper bound in cents (exclusive). `null` = no upper bound. */
  maxAmount: number | null;
  /** Location's share in this tier, 0-100. */
  percentage: number | null;
  /** Flat fee vGrubs charges in this tier, in cents. */
  flatFee: number | null;
  /** Exceptions inside this tier. Providers left out get the tier's values. */
  providerRules: PayoutProviderRule[];
}

export interface PayoutConfigResponse {
  service: LocationPayoutService;
  /** Human readable service name, ready to display. */
  serviceLabel: string;
  model: PayoutModelType;
  /** Location's share of the base amount, 0-100. */
  percentage: number | null;
  /** Flat amount vGrubs charges per order, in cents. */
  flatFee: number | null;
  /** Sorted by `minAmount` ascending. Only used by the hybrid model. */
  tiers: PayoutTier[];
  /**
   * Exceptions to this config. Only the `vGrubs` service supports them - the
   * other services take orders from a single provider, so the API rejects any.
   * On the hybrid model these cover the base amounts no tier matches; a tier
   * carries its own list.
   */
  providerRules: PayoutProviderRule[];
}

export interface LocationEntity {
  id: number;
  imageUrl: string | null;
  storeId: number;
  verified: boolean;
  payoutConfigs: PayoutConfigResponse[];
  coordinates: Coordinates;
  owner: Owner;
  configuration: Configuration;
  name: string;
  address: string;
  // Suite / unit inside a plaza. Never part of `address` - glue it on with
  // addressWithUnitNumber() when rendering.
  unitNumber: string | null;
  city: string;
  state?: string | null;
  country: string;
  zipCode: string;
  phone?: string | null;
  menuSignInProviders: SimpleMenuProviderEntity[];
  /** Admin GET only: the partner's POS + marketplace access, passwords stripped. */
  integrationAccess?: IntegrationAccess | null;
  businessLicense?: string | null;
  crmApiKey?: string | null;
  brands: Brand[];
  images: LocationImageEntity[];
  providers: Array<{
    type: Provider;
  }>;
  notes?: string | null;
  earningsAvailable: boolean;
  createdAt: string;
  achCustomerId?: string | null;
  menuConsultant?: string | null;
  menuInfo: MenuInfo;
  salesConfig?: SalesConfig | null;
  payoutDelayHours: number;
  salesStatistics?: SalesStatics | null;
  earnings: ProviderValue[];
  sip?: SipDetails | null;
  referral?: Referral | null;
  referralPercentage?: number | null;
  vDriveStatus: VDriveStatus;
  vDriveProviderContracts: Array<LocationVDriveProviderContractEntity>;
  autoPayoutType: AutoPayoutType;
  includeServerFeeDeduction: boolean;
  includeDiscountDeduction: boolean;
  includeVDrivePayoutDeduction: boolean;
  reportBasedPayout: boolean;
  vOrdersOrderingUrl?: string | null;
  /** Review link after a 4-5 star rating on the location's own brand. */
  googleBusinessUrl?: string | null;
  mode: LocationMode;
  deleted: boolean;
  /**
   * Admin `GET /v1/locations/:id` only (not v2): what the location owes,
   * null when nothing. Optional: a backend from before Oct 26, 2026 does not
   * send it.
   */
  outstanding?: LocationOutstandingSummary | null;
}

/**
 * The light view of a location's debt for Location Details. Same amounts as
 * its Outstanding Balances page; cents throughout. `total` is always the sum
 * of the four parts.
 */
export type LocationOutstandingSummary = {
  total: number;
  /** Open vDrive items, less what was already repaid. */
  previousTransactionDueBalance: number;
  /** The vDrive negative balance; the whole of it on a shared balance. */
  reserveUsed: number;
  negativeFeeDue: number;
  /** Open SaaS fees, less what was already repaid. */
  saas: number;
  /**
   * The oldest open item or the start of the negative balance, whichever is
   * earlier. Items moved over from the old system carry the move date (Sep
   * 2026), not the day the debt started.
   */
  oldestItemAt: string | null;
  /** Bank payments of this debt in progress, fees included. */
  pendingBankTotal: number;
  /** `remaining` leaves out what a bank payment is taking right now. */
  activePlans: { service: 'vDrive' | 'saas'; remaining: number }[];
  /** Every location on the vDrive balance, this one included. */
  vDriveBalanceLocations: { id: number; name: string }[];
  /**
   * The backend's rule (today: deleted and owes) for the collection letter
   * and the call summary. Read it, never work it out.
   */
  collectionLetterAvailable: boolean;
};

export enum LocationMode {
  tablet = 'tablet',
  pos = 'pos',
}

export enum AutoPayoutType {
  none = 'none',
  weekly = 'weekly',
  monthly = 'monthly',
}

export type EarningPlanEntity = {
  id: number;
  year: number | null;
  endAt: string | null; // ISO date-time string
  amount: number;
  active: boolean;
  expired: boolean;
};

export type AgentLevel = {
  level: number;
  locationsCount: number;
  price: number;
  nextLevelCount: number;
  nextLevelAmount: number;
};

export type AgentDetails = {
  earningPlans: EarningPlanEntity[];
  agentLevel: AgentLevel;
  vOrdersCommision: number | null;
  vDriveCommission: number | null;
  vDriveService: boolean;
  vOrdersService: boolean;
  vGrubsService: boolean;
};

export type UpdateWorkingHoursLocationV2Dto = {
  business: WorkingHoursResponseV2[];
  delivery: WorkingHoursResponseV2[];
  applyToAllBrands?: boolean;
};

export type WorkingHoursReponseV2 = {
  business: WorkingHoursResponseV2[];
  delivery: WorkingHoursResponseV2[];
};

export type WorkingHoursResponseV2 = {
  day: DayOfWeek;
  startTime: string;
  endTime: string;
};

export enum DayOfWeek {
  Sunday = 'SUNDAY',
  Monday = 'MONDAY',
  Tuesday = 'TUESDAY',
  Wednesday = 'WEDNESDAY',
  Thursday = 'THURSDAY',
  Friday = 'FRIDAY',
  Saturday = 'SATURDAY',
}

export type UpdateHolidayHoursLocationV2Dto = {
  holidayHours: HolidayHoursReponseV2[];
  applyToAllBrands?: boolean;
};

export type HolidayHoursReponseV2 = {
  date: string;
  startTime: string;
  endTime: string;
};

export type AllClosingDaysV2 = {
  timeZone: string;
  items: BrandClosingDayEntity[];
};

export type BrandClosingDayEntity = {
  id: number;
  date: string;
  day: string;
};

export type LocationVDriveEntity = {
  id: number;
  autoRechargeAmount?: number | null;
  autoRechargeWhenAmount?: number | null;
  createdAt: string;
  freeDeliveries?: number | null;
  location: SimpleLocationEntity;
  owner: VDriveLocationOwnerEntity;
  merchantId?: string | null;
  status: VDriveStatus;
  /** Why an admin rejected it; set only while `status` is `rejected`. */
  rejectionReason?: string | null;
  reservedBalance?: number | null;
  totalBalance?: number | null;
  stripeCustomerId?: string | null;
  autoAssign: boolean;
  vGrubsBrandsEnabled: boolean;
  providers: VDriveProviderInfo[];
  isVDriveCard?: boolean | null;
  /** The primary card. */
  last4?: string | null;
  /**
   * The secondary card, charged for the same amount when the primary
   * declines. Null when there is none; undefined from a backend without it
   * (the dev admin API did not send it yet on Sep 30, 2026).
   */
  secondaryLast4?: string | null;
  cardFailed?: boolean | null;
  achCustomerId?: string | null;
  vDriveFee?: number | null;
  vGrubsFee?: number | null;
  fundedTipSchedule: FundedTipScheduleEntity[];
  deliveryAssignment?: DeliveryAssignmentEntity | null;
  cwTeamId?: string | null;
  /**
   * The vDrive fleet this is pinned to ("VD-10012"): its quote is asked first
   * and always wins. `null` when not pinned.
   */
  vdriveOrganizationId?: string | null;
  /** The pinned fleet's name as it was when saved. */
  vdriveOrganizationName?: string | null;
  exclusiveOfferEnabled: boolean;
  callDispatcherEnabled: boolean;
  callDispatcherMinutes?: number | null;
  motoClickEnabled?: boolean | null;
  /**
   * Pickup note handed to the delivery company and shown to the driver.
   * `null` when none is set. Max 280 chars - longer is a 400 from the backend.
   */
  pickupInstructions?: string | null;
  /**
   * How far the balance may go negative when the owner's card fails, in cents
   * - the effective one: the reserve level's ($100 / $200 / $300) when no admin
   * set another.
   */
  overdraftLimit: number;
  /** The limit an admin set for this balance; `null` means by reserve level. */
  customOverdraftLimit: number | null;
  /** `null` without a balance; undefined from a backend before Oct 27, 2026. */
  paymentMode?: VDrivePaymentMode | null;
  /**
   * 1, 2 or 3 ($100 / $200 / $300). Goes up by one a day on its own, back to 1
   * on a pause for being negative or a returned bank payment. Means nothing
   * while `customOverdraftLimit` is set.
   */
  reserveLevel?: number | null;
  /** When the level last changed. */
  reserveLevelChangedAt?: string | null;
  /** How far the balance is below zero, in cents; 0 when it is not. */
  outstandingAmount: number;
  /** Bank (ACH) payments in progress for this balance. */
  pendingBank: PendingBankSummary;
};

export type DeliveryAssignmentEntity = {
  times: DeliveryAssignmentTimeEntity[];
  preferredAssignmentType: VDrivePreferredAssignmentType;
  bannedProviders: VDriveProvider[];
  providers: DeliveryAssignmentProviderEntity[];
  nonOperatingConditions: DeliveryAssignmentNonOperatingConditionsEntity[];
  ignoreGlobalStrategies: boolean;
  /**
   * DeliveryNow auto-reassign. The whole `deliveryAssignment` object is saved as
   * a replace, not a merge - leave these out of a PUT and the backend resets the
   * rule - so both are required here and every payload has to carry them.
   */
  deliveryNowReassignEnabled: boolean;
  /** Whole minutes, 1-100. Required by the backend once the toggle is on. */
  deliveryNowReassignMinutes: number | null;
};

export type DeliveryAssignmentTimeEntity = {
  id?: number;
  amount?: number | null;
  startTime?: string | null;
  endTime?: string | null;
  minutes?: number | null;
  provider?: VDriveProvider;
};

export type DeliveryAssignmentProviderEntity = {
  id?: number;
  provider: VDriveProvider;
  distanceMiles?: number;
  startTime?: string;
  endTime?: string;
  orderProviders?: Provider[];
  fallbackProviders?: VDriveProvider[];
};

export type DeliveryAssignmentNonOperatingConditionsEntity = {
  id?: number;
  day: WeekDay;
  distanceMiles?: number;
  startTime?: string;
  endTime?: string;
  orderProvider?: Provider;
};

export type VDriveLocationOwnerEntity = {
  id: number;
  firstName?: string | null;
  lastName?: string | null;
  phoneNumber: string;
  role: UserRole;
};

export type VDriveProviderInfo = {
  provider: Provider;
  enabled: boolean;
};

export type FundedTipScheduleEntity = {
  dayOfTheWeek: number;
  times: FundedTipTimeScheduleEntity[];
};

export type FundedTipTimeScheduleEntity = {
  amount: number;
  startTime: string;
  endTime: string;
};

export type ScheduleEntity = {
  dayOfTheWeek?: number;
  amount: number;
  startTime: string;
  endTime: string;
  index?: number;
};

export type SimpleLocationWithTodayDataResponse = {
  location: SimpleLocationEntity;
  todaysOrders: number;
  todaysSales: number;
};

export type LocationServiceConfig = {
  location: SimpleLocationEntity;
  vDriveCommission: number | null;
  vDriveDeliveryCharge: number | null;
  vDriveDeliveryFee: number | null;
  vOrdersAgentCommission: number | null;
  vOrdersFee: number | null;
  vGrubsService: boolean;
  vGrubsCommission: number | null;
  extraPrinters: number;
  extraTablets: number;
  vDriveService: boolean;
  vOrdersService: boolean;
  vRevenueService: boolean;
  revenueRecoveryFee: number | null;
};

export enum WeekDay {
  Monday = 'monday',
  Tuesday = 'tuesday',
  Wednesday = 'wednesday',
  Thursday = 'thursday',
  Friday = 'friday',
  Saturday = 'saturday',
  Sunday = 'sunday',
}

export type AgentCustomerStatsResponse = {
  vGrubs: AgentCustomersStatsVGrubsResponse[];
  vDrive: AgentCustomersStatsVDriveResponse[];
  vGrubsTotal: number;
  vDriveTotal: number;
  customersOverview: CustomersOverviewEntity;
  recovery: AgentCustomersStatsRecoveryResponse[];
  recoveryTotal: number;
};

export type AgentCustomersStatsVGrubsResponse = {
  locationId: number;
  locationName: string;
  generatedAmount: number;
};

export type AgentCustomersStatsVDriveResponse = {
  locationId: number;
  locationName: string;
  totalGain: number;
  totalIncreaseInGrossSales: number;
  totalVDriveCost: number;
};

export type AgentCustomersStatsRecoveryResponse = {
  locationId: number;
  locationName: string;
  generatedAmount: number;
};

export enum ApplyToType {
  location_only = 'location_only',
  brand_only = 'brand_only',
  all_brands = 'all_brands',
  location_and_all_brands = 'location_and_all_brands',
}
